import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Avatar, Box, CircularProgress, IconButton, MenuItem, TextField, } from "@mui/material";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSearchPlayers, useSearchTeams } from "../ApiProvider";
import { useDebounce } from "../utils/useDebounce";
const DEBOUNCE_TIME = 500;
var SearchType;
(function (SearchType) {
    SearchType[SearchType["Players"] = 0] = "Players";
    SearchType[SearchType["Teams"] = 1] = "Teams";
})(SearchType || (SearchType = {}));
export const SearchBar = () => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchType, setSearchType] = useState(SearchType.Teams);
    return (_jsx(_Fragment, { children: searchOpen ? (_jsxs(Box, { sx: { display: "flex", alignItems: "center", ml: 1, height: 40 }, children: [_jsxs(TextField, { select: true, value: searchType, onChange: (e) => setSearchType(Number(e.target.value)), size: "small", sx: {
                        width: 120,
                        bgcolor: "white",
                        "&:hover": { bgcolor: "#f0f0f0" },
                        borderRadius: 1,
                        fontSize: "0.875rem",
                        height: 36,
                    }, children: [_jsx(MenuItem, { value: SearchType.Teams, children: _jsx("div", { "data-testid": "teams-option", children: "Teams" }) }), _jsx(MenuItem, { value: SearchType.Players, children: _jsx("div", { "data-testid": "players-option", children: "Players" }) })] }), searchType === SearchType.Players ? (_jsx(PlayerSearchAutocomplete, { setSearchOpen: setSearchOpen })) : (_jsx(TeamSearchAutocomplete, { setSearchOpen: setSearchOpen })), _jsx(IconButton, { onClick: () => setSearchOpen(false), children: _jsx(CloseIcon, { sx: { color: "white" } }) })] })) : (_jsx(IconButton, { "data-testid": "search-button", color: "inherit", onClick: () => setSearchOpen(true), children: _jsx(SearchIcon, {}) })) }));
};
const PlayerSearchAutocomplete = ({ setSearchOpen, }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_TIME);
    const { data, isLoading } = useSearchPlayers({
        searchTerm: debouncedSearchQuery,
        enabled: debouncedSearchQuery.length > 2,
    });
    const onSearchChange = (_event, newValue) => {
        setSearchQuery(newValue || "");
    };
    const isLoadSearching = isLoading && searchQuery.length > 2;
    const players = data?.results;
    const navigate = useNavigate();
    const onSearchSubmit = () => {
        if (searchQuery.trim()) {
            navigate(`/search/players/?q=${encodeURIComponent(searchQuery)}`);
        }
    };
    return (_jsx(Autocomplete, { options: players || [], loading: isLoadSearching, open: !!players && players.length > 0, filterOptions: (x) => x, popupIcon: null, sx: { ml: 1 }, getOptionLabel: (option) => `${option.firstName} ${option.lastName}`, onInputChange: onSearchChange, renderInput: (params) => (_jsx(TextField, { ...params, placeholder: "Search Players", size: "small", variant: "outlined", "data-testid": "search-input", sx: {
                bgcolor: "white",
                borderRadius: 2,
                minWidth: 300,
            }, onKeyDown: (e) => {
                if (e.key === "Enter") {
                    onSearchSubmit();
                }
            }, InputProps: {
                ...params.InputProps,
                endAdornment: (_jsxs(_Fragment, { children: [isLoadSearching ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : null, _jsx(IconButton, { sx: { p: 0.5 }, onClick: onSearchSubmit, children: _jsx(SearchIcon, { fontSize: "small" }) }), params.InputProps.endAdornment] })),
            } })), renderOption: (props, option) => {
            const { key, ...optionProps } = props;
            return (_jsx(Link, { to: `/players/${option.id}`, style: { textDecoration: "none", color: "inherit", width: "100%" }, onClick: () => setSearchOpen(false), children: _jsxs(Box, { component: "li", ...optionProps, sx: {
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        "&:hover": { bgcolor: "#f0f0f0" },
                    }, children: [_jsx(Avatar, { src: option.picture ?? undefined }), _jsxs("span", { children: [option.firstName, " ", option.lastName] })] }) }, key));
        } }));
};
const TeamSearchAutocomplete = ({ setSearchOpen, }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const onSearchChange = (_event, newValue) => {
        setSearchQuery(newValue || "");
    };
    const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_TIME);
    const { data, isLoading } = useSearchTeams({
        searchTerm: debouncedSearchQuery,
        enabled: debouncedSearchQuery.length > 2,
    });
    const isLoadSearching = isLoading && searchQuery.length > 2;
    const teams = data?.results;
    const onSearchSubmit = () => {
        if (searchQuery.trim()) {
            navigate(`/search/teams/?q=${encodeURIComponent(searchQuery)}`);
        }
    };
    return (_jsx(Autocomplete, { options: teams || [], loading: isLoadSearching, open: !!teams && teams.length > 0, getOptionLabel: (option) => option.name, filterOptions: (x) => x, sx: { ml: 1 }, onInputChange: onSearchChange, popupIcon: null, renderInput: (params) => (_jsx(TextField, { ...params, placeholder: "Search Teams", size: "small", variant: "outlined", "data-testid": "search-input", sx: {
                bgcolor: "white",
                borderRadius: 2,
                minWidth: 300,
            }, onKeyDown: (e) => {
                if (e.key === "Enter") {
                    onSearchSubmit();
                }
            }, InputProps: {
                ...params.InputProps,
                endAdornment: (_jsxs(_Fragment, { children: [isLoadSearching ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : null, _jsx(IconButton, { sx: { p: 0.5 }, onClick: onSearchSubmit, children: _jsx(SearchIcon, { fontSize: "small" }) }), params.InputProps.endAdornment] })),
            } })), renderOption: (props, option) => {
            const { key, ...optionProps } = props;
            return (_jsx(Link, { to: `/teams/${option.id}`, style: { textDecoration: "none", color: "inherit", width: "100%" }, onClick: () => setSearchOpen(false), children: _jsxs(Box, { component: "li", ...optionProps, sx: {
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        "&:hover": { bgcolor: "#f0f0f0" },
                    }, children: [_jsx(Avatar, { src: option.logo || undefined }), _jsx("span", { children: option.name })] }) }, key));
        } }));
};
